import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Dialog,
  Fade,
  Zoom,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { AccessTime } from '@material-ui/icons';

const InactivityCountdown = ({ secondsRemaining, onUserActivity, onTimeout }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [restaurante, setRestaurante] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Carrega dados do restaurante (corPrimaria)
  useEffect(() => {
    const loadRestaurante = async () => {
      try {
        const cache = localStorage.getItem('restaurante');
        if (cache) {
          setRestaurante(JSON.parse(cache));
        } else {
          // Mock data para desenvolvimento
          const mockData = { corPrimaria: '#fa5b6d' };
          setRestaurante(mockData);
          localStorage.setItem('restaurante', JSON.stringify(mockData));
        }
      } catch (err) {
        console.error('Erro ao carregar restaurante', err);
        setRestaurante({ corPrimaria: '#fa5b6d' });
      }
    };
    loadRestaurante();
  }, []);

  // Mostra o modal se estiver nos últimos 10 segundos
  useEffect(() => {
    setIsVisible(secondsRemaining > 0 && secondsRemaining <= 10);
  }, [secondsRemaining]);

  const handleDialogClick = useCallback(() => {
    onUserActivity();
    setIsVisible(false);
  }, [onUserActivity]);

  const primaryColor = restaurante.corPrimaria || '#fa5b6d';
  const progressPercentage = ((10 - secondsRemaining) / 10) * 100;
  const isUrgent = secondsRemaining <= 3;

  // Responsividade baseada no tamanho da tela
  const dialogSize = {
    width: isMobile ? '90vw' : '70vw',
    maxWidth: isMobile ? '400px' : '800px',
    minHeight: isMobile ? '50vh' : '60vh',
  };

  const fontSizes = {
    title: isMobile ? '2rem' : '3.5rem',
    counter: isMobile ? '4rem' : '8rem',
    instruction: isMobile ? '1.2rem' : '2rem',
    icon: isMobile ? 80 : 160,
  };

  return (
    <Dialog
      open={isVisible}
      onClose={handleDialogClick}
      maxWidth={false}
      fullWidth={false}
      PaperProps={{
        style: {
          ...dialogSize,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          color: '#fff',
          borderRadius: 32,
          padding: isMobile ? '32px' : '64px',
          textAlign: 'center',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.6)',
          border: `4px solid ${primaryColor}`,
          position: 'relative',
          overflow: 'hidden',
        }
      }}
      BackdropProps={{ 
        onClick: handleDialogClick,
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)',
        }
      }}
      TransitionComponent={Zoom}
      transitionDuration={400}
    >
      {/* Barra de progresso no topo */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 8,
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '32px 32px 0 0',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${progressPercentage}%`,
            backgroundColor: isUrgent ? '#ff4444' : primaryColor,
            transition: 'all 0.3s ease, background-color 0.5s ease',
            backgroundImage: `linear-gradient(45deg, transparent 25%, rgba(255,255,255,0.2) 25%, rgba(255,255,255,0.2) 50%, transparent 50%, transparent 75%, rgba(255,255,255,0.2) 75%)`,
            backgroundSize: '20px 20px',
            animation: 'progressStripes 1s linear infinite',
          }}
        />
      </Box>

      {/* Gradiente de fundo animado */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at center, ${primaryColor}15 0%, transparent 70%)`,
          animation: isUrgent ? 'pulse 1s ease-in-out infinite' : 'none',
          zIndex: 0,
        }}
      />

      {/* Conteúdo principal */}
      <Fade in={isVisible} timeout={600}>
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center"
          gap={isMobile ? 3 : 5}
          sx={{ 
            position: 'relative', 
            zIndex: 1,
            minHeight: isMobile ? '40vh' : '50vh',
          }}
        >
          {/* Ícone animado */}
          <Box
            sx={{
              animation: isUrgent ? 'shake 0.5s ease-in-out infinite' : 'none',
              filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5))',
            }}
          >
            <AccessTime 
              style={{ 
                fontSize: fontSizes.icon, 
                color: isUrgent ? '#ff4444' : primaryColor,
                transition: 'color 0.3s ease',
              }} 
            />
          </Box>

          {/* Título */}
          <Typography 
            variant="h3" 
            style={{ 
              fontWeight: 600,
              fontSize: fontSizes.title,
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.7)',
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
            }}
          >
            Sessão prestes a expirar
          </Typography>

          {/* Contador principal */}
          <Box sx={{ position: 'relative' }}>
            <Typography 
              variant="h1" 
              style={{ 
                fontWeight: 900,
                fontSize: fontSizes.counter,
                color: isUrgent ? '#ff4444' : primaryColor,
                textShadow: '0 4px 8px rgba(0, 0, 0, 0.7)',
                lineHeight: 1,
                letterSpacing: '-0.05em',
                transition: 'all 0.3s ease',
                animation: isUrgent ? 'counterPulse 0.8s ease-in-out infinite' : 'none',
              }}
            >
              {secondsRemaining}
            </Typography>
            
            {/* Texto "segundos" menor */}
            <Typography 
              variant="h5" 
              style={{ 
                fontWeight: 400,
                fontSize: fontSizes.instruction * 0.8,
                opacity: 0.9,
                marginTop: isMobile ? '8px' : '16px',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.7)',
              }}
            >
              segundo{secondsRemaining !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {/* Instrução de toque */}
          <Box 
            display="flex" 
            alignItems="center" 
            gap={2}
            sx={{
              animation: 'bounce 2s ease-in-out infinite',
              marginTop: isMobile ? 2 : 4,
            }}
          >
            <Typography 
              variant="h6" 
              style={{ 
                fontWeight: 500,
                fontSize: fontSizes.instruction,
                opacity: 0.95,
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.7)',
                lineHeight: 1.3,
              }}
            >
              Toque em qualquer lugar para continuar
            </Typography>
          </Box>

          {isUrgent && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '120%',
                height: '120%',
                border: `3px solid #ff4444`,
                borderRadius: '50%',
                animation: 'urgentRing 1s ease-in-out infinite',
                zIndex: -1,
              }}
            />
          )}
        </Box>
      </Fade>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes progressStripes {
          0% { background-position: 0 0; }
          100% { background-position: 20px 0; }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }
        
        @keyframes counterPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        
        @keyframes urgentRing {
          0% { 
            transform: translate(-50%, -50%) scale(1); 
            opacity: 1; 
          }
          100% { 
            transform: translate(-50%, -50%) scale(1.1); 
            opacity: 0; 
          }
        }
      `}</style>
    </Dialog>
  );
};

export default InactivityCountdown;